import React, { useEffect, useState } from 'react';
import { Mail, Trash2, Check, Eye, Loader2, AlertCircle } from 'lucide-react';
import { adminApi } from '../../api';
import { PageHeader, Table, Modal } from './AdminUI';

const STATUS_COLORS = {
  queued: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800'
};

const KIND_LABELS = {
  partner_confirm: 'Conferma partner',
  partner_alert: 'Alert staff',
  newsletter: 'Newsletter'
};

export default function AdminNotifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('');

  const load = () => { setLoading(true); adminApi.notifications().then(setItems).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const rows = filter ? items.filter(n => n.status === filter) : items;
  const counts = {
    queued: items.filter(i => i.status === 'queued').length,
    sent: items.filter(i => i.status === 'sent').length,
    failed: items.filter(i => i.status === 'failed').length
  };

  const markSent = async (row) => { await adminApi.markNotificationSent(row.id); load(); if (selected?.id === row.id) setSelected({ ...selected, status: 'sent' }); };
  const del = async (row) => { if (!window.confirm('Eliminare questa notifica?')) return; await adminApi.deleteNotification(row.id); setSelected(null); load(); };

  return (
    <div>
      <PageHeader
        title="Notifiche"
        subtitle={`${items.length} email — ${counts.queued} in coda, ${counts.sent} inviate, ${counts.failed} fallite`}
      />

      {counts.queued > 0 && (
        <div className="mb-4 flex items-start gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm">
          <AlertCircle size={18} className="mt-0.5 flex-shrink-0"/>
          <div>
            <p className="font-semibold">{counts.queued} email in coda</p>
            <p className="text-xs mt-1 text-amber-800/80">
              SMTP non configurato — le notifiche restano in coda. Per l'invio automatico configura in <code className="bg-white px-1 rounded">/app/backend/.env</code>: <code className="bg-white px-1 rounded">SMTP_HOST</code>, <code className="bg-white px-1 rounded">SMTP_PORT</code>, <code className="bg-white px-1 rounded">SMTP_USER</code>, <code className="bg-white px-1 rounded">SMTP_PASS</code>, <code className="bg-white px-1 rounded">SMTP_FROM</code>, <code className="bg-white px-1 rounded">YUP_NOTIFY_EMAIL</code>. Puoi comunque contattare manualmente i partner e marcare le email come "Inviate".
            </p>
          </div>
        </div>
      )}

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setFilter('')}
                data-testid="notif-filter-all"
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${!filter ? 'bg-[color:var(--yup-primary)] text-black' : 'bg-gray-100 text-gray-600'}`}>
          Tutte ({items.length})
        </button>
        {Object.keys(STATUS_COLORS).map(s => (
          <button key={s} onClick={() => setFilter(s)}
                  data-testid={`notif-filter-${s}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter === s ? 'bg-[color:var(--yup-primary)] text-black' : STATUS_COLORS[s]}`}>
            {s === 'queued' ? 'In coda' : s === 'sent' ? 'Inviate' : 'Fallite'} ({counts[s] || 0})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500"><Loader2 className="inline animate-spin" size={20}/> Loading...</div>
      ) : (
        <Table
          columns={[
            { key: 'kind', label: 'Tipo', render: r => <span className="text-xs font-semibold text-gray-700">{KIND_LABELS[r.kind] || r.kind}</span> },
            { key: 'to_email', label: 'Destinatario', render: r => <span className="text-blue-600 font-mono text-xs">{r.to_email}</span> },
            { key: 'subject', label: 'Oggetto', render: r => <span className="text-sm">{r.subject}</span> },
            { key: 'status', label: 'Stato', render: r => (
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_COLORS[r.status] || 'bg-gray-100'}`}>
                {r.status === 'queued' ? 'In coda' : r.status === 'sent' ? 'Inviata' : 'Fallita'}
              </span>
            )},
            { key: 'created_at', label: 'Creata', render: r => r.created_at ? new Date(r.created_at).toLocaleString('it-IT') : '—' }
          ]}
          rows={rows}
          actions={row => (
            <div className="flex gap-1 justify-end">
              <button onClick={() => setSelected(row)} data-testid={`notif-preview-${row.id}`} className="p-1.5 hover:bg-gray-100 rounded text-blue-600" title="Anteprima"><Eye size={15}/></button>
              {row.status !== 'sent' && (
                <button onClick={() => markSent(row)} data-testid={`notif-mark-sent-${row.id}`} className="p-1.5 hover:bg-green-100 rounded text-green-700" title="Marca come inviata"><Check size={15}/></button>
              )}
              <button onClick={() => del(row)} data-testid={`notif-delete-${row.id}`} className="p-1.5 hover:bg-red-100 rounded text-red-600"><Trash2 size={15}/></button>
            </div>
          )}
        />
      )}

      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected?.subject} maxWidth="max-w-3xl">
        {selected && (
          <div>
            <div className="mb-4 text-sm text-gray-600 flex items-center gap-2">
              <Mail size={14}/> <span className="font-mono">{selected.to_email}</span>
              <span className="mx-2">·</span>
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_COLORS[selected.status]}`}>{selected.status}</span>
            </div>
            {selected.error && (
              <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-800 text-xs font-mono">{selected.error}</div>
            )}
            <div className="border rounded-lg overflow-hidden bg-gray-50">
              <iframe title="email preview" srcDoc={selected.html_body} sandbox="allow-same-origin" className="w-full h-[500px] bg-white"/>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
